<template>
    <div class="container">
        <div class="row mt-3">
            <div class="col-4">
                <h4>{{ $t('create') }} {{ $t('group') }}</h4>
                <input type="button" class="btn btn-primary" :value="$t('create') + ' ' + $t('group')"
                    @click="showGroupModal = true" />
                <GroupModal :show-modal="showGroupModal" @close-modal="showGroupModal = false"
                    @data-updated="dataUpdated" />
            </div>
            <div class="col-4">
                <h4>{{ $t('edit') }} {{ $t('group_info') }}</h4>
                <input type="button" class="btn btn-primary" :value="$t('edit') + ' ' + $t('group_info')"
                    @click="showEditModal = true" />
                <GroupEditModal :show-modal="showEditModal" :data="groups"
                    :target="{ name: 'groups', label: 'group_name', track_by: 'group_id' }"
                    @close-modal="showEditModal = false" @data-updated="dataUpdated" />
            </div>
            <div class="col-4">
                <h4>{{ $t('delete') }} {{ $t('group') }}</h4>
                <input type="button" class="btn btn-primary" :value="$t('delete') + ' ' + $t('groups')"
                    @click="showDeleteModal = true" />
                <DeleteModal :show-modal="showDeleteModal" :data="groups"
                    :target="{ name: 'groups', label: 'group_name', track_by: 'group_id' }"
                    @close-modal="showDeleteModal = false" @data-updated="dataUpdated" />
            </div>
        </div>
        <div class="row mt-2">
            <div class="col-6">
                <h4>{{ $t('edit_users_for_groups') }}</h4>
            </div>
        </div>
        <div class="row">
            <div class="col-6">
                <div class="row mt-2">
                    <span>{{ labelText($t('select'), $t('group'), $t('groups'), $t('or')) }}</span>
                    <SearchTool :placeholder="$t('search') + ' ' + $t('groups') + '...'" :data="groups"
                        label="group_name" track-by="group_id" @select="handleGroupSelect" />
                </div>
                <div class="row mt-2">
                    <div class="d-flex align-items-center">
                        <select id="group-action-select" class="form-select me-2" v-model="selectedOption">
                            <option value="add_selected">{{ $t('add_selected') }}</option>
                            <option value="remove_selected">{{ $t('remove_selected') }}</option>
                            <option value="remove_all">{{ $t('remove_all') }}</option>
                            <option value="copy_all">{{ $t('copy_all') }}</option>
                            <option value="move_all">{{ $t('move_all') }}</option>
                        </select>
                        <select id="group-scope-select" class="form-select me-2" v-model="selectedScope">
                            <option value="users">{{ $t('users_exact') }}</option>
                            <option value="devices">{{ $t('devices_exact') }}</option>
                        </select>
                        <span v-if="selectedOption == 'move_all' || selectedOption == 'copy_all'">{{ $t('from_group')
                            }}</span>
                        <span v-else></span>
                    </div>
                </div>
                <div class="row mt-2">
                    <div v-if="selectedOption !== 'remove_all'">
                        <SearchTool v-if="selectedOption == 'copy_all' || selectedOption == 'move_all'"
                            :placeholder="$t('search') + ' ' + $t('groups') + '...'" :data="groups" label="group_name"
                            track-by="group_id" @select="handleSourceSelect" />
                        <SearchTool
                            v-else-if="(selectedOption != 'copy_all' || selectedOption != 'move_all') && selectedScope == 'users'"
                            :placeholder="$t('search') + ' ' + $t('users') + '...'" :data="users" label="user_name"
                            track-by="user_id" @select="handleUserSelect" />
                        <SearchTool
                            v-else-if="(selectedOption != 'copy_all' || selectedOption != 'move_all') && selectedScope == 'devices'"
                            :placeholder="$t('search') + ' ' + $t('devices') + '...'" :data="devices"
                            label="device_name" track-by="device_id" @select="handleDeviceSelect" />

                    </div>
                </div>
                <div class="row mt-2">
                    <div class="col-6">
                        <input type="button" class="btn btn-success" :value="$t('save_changes')" @click="saveChanges" />
                    </div>
                </div>
                <div class="row mt-2">
                    <div v-if="message" :class="alertClass">
                        {{ message }}
                    </div>
                </div>
            </div>

            <div class="col-6">
                <div class="row mt-2" v-if="selectedGroups.length > 0">
                    <div v-for="group in selectedGroups" :key="group.group_id">
                        <GroupCard :group="group" :key="i"/>
                    </div>
                </div>
                <div v-else class="row mt-2">
                    <GroupCard :group="{}" />
                </div>
            </div>
        </div>
        <div v-if="groupsWithNoUsers && groupsWithNoUsers.length > 0" class="row mt-3 w-50">
            <h4>{{ $t('groups_no_users') }}</h4>
            <div v-for="group in groupsWithNoUsers" :key="group.group_id">{{ group.group_name }}</div>
        </div>
        <div v-if="groupsWithNoDevices && groupsWithNoDevices.length > 0" class="row mt-3 w-50">
            <h4>{{ $t('groups_no_devices') }}</h4>
            <div v-for="group in groupsWithNoDevices" :key="group.group_id">{{ group.group_name }}</div>
        </div>
    </div>
</template>

<script>
const { getCookie } = require('@/helpers');
import axios from 'axios';
import { fetchData } from '@/helpers';
import SearchTool from './SearchTool.vue';
import GroupCard from './GroupCard.vue';
import GroupModal from './GroupModal.vue';
import GroupEditModal from './GroupEditModal.vue';
import DeleteModal from './DeleteModal.vue';

export default {
    name: 'GroupTools',
    components: { SearchTool, GroupCard, GroupModal, GroupEditModal, DeleteModal },
    props: {
        users: {
            type: Array,
            default: () => []
        },
        groups: {
            type: Array,
            default: () => []
        },
        devices: {
            type: Array,
            default: () => []
        },
    },
    data() {
        return {
            selectedUsers: [],
            selectedGroups: [],
            selectedDevices: [],
            selectedSources: [],
            selectedOption: 'add_selected',
            selectedScope: 'users',
            showGroupModal: false,
            showDeleteModal: false,
            showEditModal: false,
            deviceGroups: [],
            userGroups: [],
            groupsWithNoUsers: [],
            groupsWithNoDevices: [],
            message: '',
            alertClass: '',
            i: 0
        };
    },
    watch: {
        devices: {
            handler(newVal, oldVal) {
                if (oldVal !== newVal) {
                    this.fetchGroupsWithNoDevices();
                }
            },
            deep: true
        },
        groups: {
            handler(newVal, oldVal) {
                if (oldVal !== newVal) {
                    this.fetchGroupsWithNoUsers();
                    this.fetchGroupsWithNoDevices();
                }
            },
            deep: true
        },
        users: {
            handler(newVal, oldVal) {
                if (oldVal !== newVal) {
                    this.fetchGroupsWithNoUsers();
                }
            },
            deep: true
        }
    },
    emits: ['data-updated'],
    methods: {
        dataUpdated() {
            this.$emit('data-updated');
            this.i++; // update key for GroupCard component
        },
        fetchData,
        // async fetchGroups() {
        //     this.userGroups = await this.fetchData('usergroups');
        //     this.deviceGroups = await this.fetchData('devicegroups');

        //     this.groups.forEach(group => {
        //         group.users = this.userGroups.filter(item => item.group_id === group.group_id);
        //         group.devices = this.deviceGroups.filter(item => item.group_id === group.group_id);
        //     });
        //     this.selectedGroups.forEach(group => {
        //         group.users = this.userGroups.filter(item => item.group_id === group.group_id);
        //         group.devices = this.deviceGroups.filter(item => item.group_id === group.group_id);
        //     });
        // },
        async fetchGroupsWithNoUsers() {
            this.userGroups = await this.fetchData('usergroups');
            const groupIdsWithUsers = new Set(this.userGroups.map(item => item.group_id));
            const groupsNoUsers = this.groups.filter(group => !groupIdsWithUsers.has(group.group_id));
            this.groupsWithNoUsers = groupsNoUsers;
            //console.log('Groups with no users:', this.groupsNoUsers);
        },
        async fetchGroupsWithNoDevices() {
            this.deviceGroups = await this.fetchData('devicegroups');
            const groupIdsWithDevices = new Set(this.deviceGroups.map(item => item.group_id));
            const groupsWithNoDevices = this.groups.filter(group => !groupIdsWithDevices.has(group.group_id));
            this.groupsWithNoDevices = groupsWithNoDevices;
            //console.log('Groups with no devices:', this.groupsWithNoDevices);
        },
        labelText(verb, object1, object2, separator) {
            return `${verb} ${object1} ${separator} ${object2}`;
        },
        handleUserSelect(selectedUsers) {
            this.selectedUsers = selectedUsers;
        },
        handleSourceSelect(selectedSources) {
            this.selectedSources = selectedSources;
        },
        handleDeviceSelect(selectedDevices) {
            this.selectedDevices = selectedDevices;
        },
        handleGroupSelect(selectedGroups) {
            this.selectedGroups = selectedGroups;
        },
        setMessage(message, isSuccess) {
            this.message = message;
            this.alertClass = isSuccess ? 'alert alert-success' : 'alert alert-warning';
        },
        async postData(topic, method, data) {
            this.message = '';
            this.alertClass = '';
            const url = `${this.$url}/${topic}`;
            try {
                const response = await axios({
                    method: method,
                    url: url,
                    headers: {
                        Authorization: 'Bearer ' + getCookie('token'),
                        ContentType: 'application/json'
                    },
                    data: data
                });
                return response;
            } catch (error) {
                console.error('Error posting data to ' + topic + ':', error);
                return error;
            }
        },
        async saveChanges() {
            let action;
            let data = [];

            if (this.selectedOption === 'add_selected' || this.selectedOption === 'remove_selected') {
                if (this.selectedScope === 'users') {
                    action = this.selectedOption.replace('_selected', '');
                    this.selectedGroups.forEach(group => {
                        this.selectedUsers.forEach(user => {
                            data.push({ user_id: user.user_id, group_id: group.group_id });
                        });
                    });
                } else if (this.selectedScope === 'devices') {
                    action = this.selectedOption;
                    this.selectedGroups.forEach(group => {
                        this.selectedDevices.forEach(device => {
                            data.push({ device_id: device.device_id, group_id: group.group_id });
                        });
                    });
                }

            } else if (this.selectedOption === 'remove_all') {
                action = 'remove_all';
                this.selectedGroups.forEach(group => {
                    data.push({ group_id: group.group_id });
                });

            } else if (this.selectedOption === 'move_all' || this.selectedOption === 'copy_all') {
                action = this.selectedOption;
                let sources = [];
                let targets = [];

                // the ones to move to
                this.selectedGroups.forEach(target => {
                    targets.push({ group_id: target.group_id });
                });
                // the ones to move from
                this.selectedSources.forEach(source => {
                    sources.push({ group_id: source.group_id });
                });
                data.push({ sources: sources, targets: targets });

            } else {
                console.error('Invalid option:', this.selectedOption);
            }

            if (action) {
                if (this.selectedScope === 'users') {
                    // edit users
                    const response = await this.postData('usergroups', 'POST', { action, data });
                    if (response instanceof Error) {
                        // Handle error response
                        this.setMessage(this.$t('error') + ': ' + response.message, false);
                    } else {
                        // Handle successful response
                        this.setMessage(this.$t('success'), true);
                        this.dataUpdated();
                        setTimeout(() => {
                            this.setMessage('', true);
                        }, 3000);
                        
                    }
                } else if (this.selectedScope === 'devices') {
                    // edit devices
                    const response = await this.postData('devicegroups', 'POST', { action, data });
                    if (response instanceof Error) {
                        // Handle error response
                        this.setMessage(this.$t('error') + ': ' + response.message, false);
                    } else {
                        // Handle successful response
                        this.dataUpdated();
                        this.setMessage(this.$t('success'), true);
                        setTimeout(() => {
                            this.setMessage('', true);
                        }, 3000);
                        // refresh data
                        
                    }
                }
            }
        },
    },
    async mounted() {
        //await this.fetchGroups();
        await this.fetchGroupsWithNoUsers();
        await this.fetchGroupsWithNoDevices();
    }
}
</script>

<style scoped>
/* Your styles here */
</style>